// CARE-160 (FEA-49): where a rehab check sends a FitOn web member back to, and with what result.
//
// FitOn web adds `returnTo`, the FitOn web path the member came from, URL-encoded, to the onboarding link.
// The link's signature covers only u, e and t, so `returnTo` is not signed: only these FitOn web paths are
// accepted, always joined to our own FitOn host. Anything else is ignored and the old links are used.
// Keep this file dependency-free so its tests stay fast.

const RETURN_TO_PATHS = [
  /^\/program\/detail\/\d+$/, // a rehab program start
  /^\/browse\/workout\/\d+$/, // a rehab workout played on its own
  /^\/rehab\/target-area\/[A-Za-z0-9][A-Za-z0-9_-]*$/, // an area id or its deeplink name
  /^\/rehab\/(my|see-all)$/,
]

// A program or a workout: a start the member can pick up again after a pass.
const RESUMABLE_PATHS = [/^\/program\/detail\/\d+$/, /^\/browse\/workout\/\d+$/]

export const isResumableReturnTo = (path) =>
  RESUMABLE_PATHS.some((pattern) => pattern.test(path))

export const CareResult = Object.freeze({ pass: 'pass', fail: 'fail' })

// Why a check failed: pain level 5, no pain in the last two weeks ("Uh oh"), or not eligible for coverage
// (Unavailable, NOT_ELIGIBLE_TALK_ABOUT_COVERAGE).
export const CareReason = Object.freeze({
  pain: 'pain',
  noPain: 'no_pain',
  notCovered: 'not_covered',
})

export const parseReturnTo = (raw) => {
  if (typeof raw !== 'string' || !raw) return null
  let path
  try {
    path = decodeURIComponent(raw)
  } catch (e) {
    return null
  }
  return RETURN_TO_PATHS.some((pattern) => pattern.test(path)) ? path : null
}

const returnToParam = (search) => {
  const match = /[?&]returnTo=([^&#]*)/.exec(search || '')
  return match ? match[1] : null
}

// The flow can move on without the query string (useFlow navigates to /about-you with state only), so the
// entry link's `returnTo` is kept for this tab: rememberReturnTo() runs once when the app loads. A new
// onboarding link without one clears it. sessionStorage can throw (blocked storage), so every use is guarded.
const RETURN_TO_KEY = 'care:rehabReturnTo'
const tabStorage = () => {
  try {
    return window.sessionStorage
  } catch (e) {
    return null
  }
}

export const rememberReturnTo = (search = window.location.search) => {
  const store = tabStorage()
  if (!store) return
  const returnTo = parseReturnTo(returnToParam(search))
  try {
    if (returnTo) store.setItem(RETURN_TO_KEY, returnTo)
    else if (/[?&]variant=/.test(search || '')) store.removeItem(RETURN_TO_KEY)
  } catch (e) {}
}

const rememberedReturnTo = () => {
  let path = null
  try {
    path = tabStorage()?.getItem(RETURN_TO_KEY) || null
  } catch (e) {}
  return path && RETURN_TO_PATHS.some((pattern) => pattern.test(path))
    ? path
    : null
}

// The `returnTo` of the onboarding link: the current page's, else the one the flow opened with. A `search`
// passed in is read alone.
export const currentReturnTo = (search) => {
  const live = parseReturnTo(
    returnToParam(search === undefined ? window.location.search : search)
  )
  if (live || search !== undefined) return live
  return rememberedReturnTo()
}

// FitOn web re-checks with the server on `careResult=pass`; the result only tells the page what to do next.
// The `?r=` form, like the old returns: in production getFitOnHost() is the app-link domain (fiton.app),
// where a path 404s in a desktop browser, but `?r=` redirects to the web app with every parameter, and
// FitOn web's start-up opens the `r` page with the others (useAppBootstrap).
export const careReturnUrl = ({ host, returnTo, success, reason }) => {
  const result = success ? CareResult.pass : CareResult.fail
  const why = !success && reason ? `&careReason=${reason}` : ''
  return `${host}?r=${returnTo.replace(/^\//, '')}&careResult=${result}${why}`
}
